import React from 'react';
import { useState, FormEvent } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Grid,
  Typography,
  Chip,
  Alert
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';

interface AuthorInput {
  name: string;
  name_ku: string;
  biography?: string;
  biography_ku?: string;
  birth_date?: Date | null;
}

export default function AddAuthor() {
  const navigate = useNavigate();
  const [author, setAuthor] = useState<AuthorInput>({
    name: '',
    name_ku: '',
    biography: '',
    biography_ku: '',
    birth_date: null
  });
  const [authorImage, setAuthorImage] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAuthor(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAuthorImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!author.name || !author.name_ku) {
      setError('Name and Kurdish name are required');
      return;
    }

    try {
      const formData = new FormData();
      
      Object.entries(author).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          if (value instanceof Date) {
            formData.append(key, value.toISOString());
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      if (authorImage) {
        formData.append('image', authorImage);
      }

      const response = await fetch('/api/authors', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create author');
      }

      setSuccess(true);
      navigate('/admin');

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <Container maxWidth="md">
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Add New Author
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Author created successfully! Redirecting...
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              label="Name"
              name="name"
              value={author.name}
              onChange={handleInputChange}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              label="Kurdish Name"
              name="name_ku"
              value={author.name_ku}
              onChange={handleInputChange}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Button
              variant="contained"
              component="label"
              fullWidth
              sx={{ mt: 2 }}
            >
              Upload Author Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
            {authorImage && (
              <Chip
                label={authorImage.name}
                onDelete={() => setAuthorImage(null)}
                sx={{ mt: 1 }}
              />
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Birth Date"
                value={author.birth_date}
                onChange={(newValue) => {
                  setAuthor(prev => ({
                    ...prev,
                    birth_date: newValue
                  }));
                }}
                sx={{ width: '100%', mt: 2 }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Biography"
              name="biography"
              value={author.biography}
              onChange={handleInputChange}
              multiline
              rows={4}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Kurdish Biography"
              name="biography_ku"
              value={author.biography_ku}
              onChange={handleInputChange}
              multiline
              rows={4}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              sx={{ mt: 3 }}
            >
              Create Author
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
